/**
 * RoomPromptBanner Component
 * 
 * Displays today's topic for a specific room, along with community theme badge.
 * Premium, calm design with proper dark mode support.
 * 
 * Updated Jan 2026: Added Day Badge (Day X/Y) for cycle topics and "Special topic" badge.
 */

import React from 'react';
import { MessageCircle, Sparkles, CalendarDays, Star } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

/**
 * DayBadge - Shows "Day X/Y" for cycle topics or "Special topic" for special overrides
 * Cycle topics include a helpful tooltip explaining the rotation
 */
const DayBadge = ({ source, cycleDay, cycleLength }) => {
  // Special topic badge (no tooltip)
  if (source === 'special') {
    return (
      <span 
        className="inline-flex items-center gap-1 px-2 py-0.5 bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300 rounded-full text-xs font-medium"
        data-testid="special-topic-badge"
      >
        <Star className="w-3 h-3" />
        Special topic
      </span>
    );
  }
  
  // Cycle topic badge with tooltip
  if (source === 'cycle' && cycleDay && cycleLength) {
    // Determine position in cycle for tooltip text
    const cyclePosition = cycleDay <= 7 ? 'early' : cycleDay <= 14 ? 'midway through' : 'late in';
    
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span 
              className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 dark:bg-slate-700/50 text-slate-600 dark:text-slate-300 rounded-full text-xs font-medium cursor-help"
              data-testid="cycle-day-badge"
            >
              <CalendarDays className="w-3 h-3" />
              Day {cycleDay}/{cycleLength}
            </span>
          </TooltipTrigger>
          <TooltipContent 
            side="top" 
            className="bg-slate-800 text-white border-slate-700 max-w-[220px] text-xs"
          >
            <p>Topics rotate every {cycleLength} days. You're {cyclePosition} the cycle today.</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
  
  return null;
};

const RoomPromptBanner = ({ 
  promptText = null,
  theme = null,
  tagline = null,
  showTheme = true,
  compact = false,
  // New props for day badge
  source = null,      // "cycle" | "special"
  cycleDay = null,    // 1..N
  cycleLength = null, // N (e.g., 21 or 28)
}) => {
  // Don't render if no prompt and no theme (or theme is "Open")
  const hasPrompt = !!promptText;
  const hasTheme = showTheme && theme && theme !== 'Open';
  const hasDayBadge = source && (source === 'special' || (source === 'cycle' && cycleDay && cycleLength));
  
  if (!hasPrompt && !hasTheme) {
    return null;
  }

  if (compact) {
    // Compact version for tight spaces
    return (
      <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="room-prompt-banner-compact">
        {hasTheme && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 rounded-full text-xs font-medium">
            <Sparkles className="w-3 h-3" />
            {theme}
          </span>
        )}
        {hasDayBadge && (
          <DayBadge source={source} cycleDay={cycleDay} cycleLength={cycleLength} />
        )}
        {hasPrompt && (
          <span className="text-gray-600 dark:text-gray-400 italic truncate max-w-xs">
            &ldquo;{promptText}&rdquo;
          </span>
        )}
      </div>
    );
  }

  return (
    <div 
      className="surface-card rounded-2xl p-4 mb-4 shadow-sm border border-amber-200/50 dark:border-amber-700/30"
      data-testid="room-prompt-banner"
    >
      {/* Theme Badge Row */}
      {(hasTheme || hasDayBadge) && (
        <div className="flex flex-wrap items-center gap-2 mb-3">
          {hasTheme && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 rounded-full text-xs font-medium">
              <Sparkles className="w-3 h-3" />
              Today&apos;s Theme: {theme}
            </span>
          )}
          {hasDayBadge && (
            <DayBadge source={source} cycleDay={cycleDay} cycleLength={cycleLength} />
          )}
          {tagline && (
            <span className="text-xs text-gray-500 dark:text-gray-400 italic">
              {tagline}
            </span>
          )}
        </div>
      )}
      
      {/* Topic Text */}
      {hasPrompt && (
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center flex-shrink-0">
            <MessageCircle className="w-4 h-4 text-amber-600 dark:text-amber-400" />
          </div>
          <div className="flex-1 min-w-0">
            <span className="text-xs font-medium text-amber-700 dark:text-amber-400 uppercase tracking-wider">
              Today&apos;s Topic
            </span>
            <p className="text-sm text-gray-800 dark:text-gray-200 mt-1 leading-relaxed">
              {promptText}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoomPromptBanner;
